import { supabase } from '../../lib/supabase';

/**
 * Service to retrieve user emails from the Supabase auth system
 */
export const userEmailService = {
  /**
   * Gets a user's email from the user_profiles table or directly from the current session
   * @param userId The ID of the user
   * @returns The user's email address
   */
  getUserEmail: async (userId: string): Promise<string> => {
    try {
      // First try to get the email from the user_profiles table
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('email')
        .eq('id', userId)
        .single();
      
      if (!profileError && profileData && profileData.email) {
        return profileData.email;
      }
      
      // If that fails, check if this is the current user
      const { data: sessionData } = await supabase.auth.getSession();
      if (sessionData.session?.user?.id === userId && sessionData.session?.user?.email) {
        return sessionData.session.user.email;
      }
      
      // As a last resort, try to get the email from metadata
      const { data: userData, error: userError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (!userError && userData) {
        // Try to update the user_profiles table with the email if we have it
        if (sessionData.session?.user?.id === userId && sessionData.session?.user?.email) {
          await supabase
            .from('user_profiles')
            .update({ email: sessionData.session.user.email })
            .eq('id', userId);
          
          return sessionData.session.user.email;
        }
      }
      
      // If all else fails, return an empty string
      console.warn(`Could not retrieve email for user ${userId}`);
      return '';
    } catch (error) {
      console.error('Error getting user email:', error);
      return '';
    }
  },
  
  /**
   * Updates a user's email in the user_profiles table
   * @param userId The ID of the user
   * @param email The email address to set
   * @returns A promise that resolves when the email is updated
   */
  updateUserEmail: async (userId: string, email: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ email })
        .eq('id', userId);
      
      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Error updating user email:', error);
      throw error;
    }
  }
};
